import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Select } from 'antd';
import { IRoute, IPageable, IClubFilters } from 'models';
import { CLUB_ROLE_CODES, userRoleLabel } from 'models/user/userRoleLabels';
import { userStore } from 'store';
import strings from 'tools';
import {
    EmptySearch,
    ErrorRequest,
    Header,
    HeaderActivity,
    Input,
    Layout,
} from 'components/layout';
import { UsersTable } from 'components/user';

import '../Users/users.scss';
import './club.scss';

interface Props {
    navigation: IRoute[];
}

const SEARCH_DEBOUNCE_MS = 400;

const DEFAULT_PAGEABLE: IPageable = { page: 0, size: 10 };

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'ACTIVE' },
    { value: 'BLOCKED', label: 'BLOCKED' },
];

const buildClubFilter = (
    search: string,
    organization: string,
    role?: string,
    userStatus?: string
): IClubFilters => {
    const next: IClubFilters = {};
    if (role) {
        next.role = role;
    }
    if (userStatus) {
        next.userStatus = userStatus;
    }
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
        next.q = trimmedSearch;
    }
    const trimmedOrg = organization.trim();
    if (trimmedOrg) {
        next.organization = trimmedOrg;
    }
    return next;
};

const filtersEqual = (a: IClubFilters, b: IClubFilters): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

const hasActiveClubFilters = (filter: IClubFilters): boolean =>
    Boolean(filter.q?.trim() || filter.role || filter.organization?.trim() || filter.userStatus);

// Раздел "Клуб" — только резиденты и амбассадоры (CD_RESIDENT + CD_INSIDER).
// Состав ролей клуба — решение бэкенда (GET /api/admin/user/club), здесь
// ничего не фильтруется на фронте.
const Club: FC<Props> = observer((props: Props) => {
    const { clubUsers, isLoadingClubUsers, errorClubUsers, successAdd } = userStore;
    const { navigation } = props;
    const [pageable, setPageable] = useState<IPageable>(userStore.clubListPageable);
    const [search, setSearch] = useState<string>(userStore.clubListSearch);
    const [organization, setOrganization] = useState<string>(userStore.clubListOrganization);
    const [filter, setFilter] = useState<IClubFilters>(userStore.clubListFilter);
    const inputRef = useRef<HTMLInputElement>(null);
    const skipInitialSearchDebounce = useRef(userStore.clubListSearch === '');
    const skipInitialOrgDebounce = useRef(userStore.clubListOrganization === '');

    useEffect(() => {
        userStore.setClubListUi(search, organization, filter, pageable);
    }, [search, organization, filter, pageable]);

    const changePage = (pageNum: number) => {
        setPageable((prev) => ({ ...prev, page: pageNum }));
    };

    const resetPageable = () => {
        setPageable((prev) =>
            prev.page === DEFAULT_PAGEABLE.page && prev.size === DEFAULT_PAGEABLE.size
                ? prev
                : DEFAULT_PAGEABLE
        );
    };

    useEffect(() => {
        userStore.getClubUsers(pageable, filter);
    }, [pageable, filter]);

    useEffect(() => {
        if (successAdd) {
            userStore.getClubUsers(pageable, filter);
            userStore.setSuccessAdd(false);
        }
    }, [successAdd]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (skipInitialSearchDebounce.current && search === '') {
                skipInitialSearchDebounce.current = false;
                return;
            }
            skipInitialSearchDebounce.current = false;

            setFilter((prev) => {
                const next = buildClubFilter(search, organization, prev.role, prev.userStatus);
                if (filtersEqual(prev, next)) {
                    return prev;
                }
                resetPageable();
                return next;
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (skipInitialOrgDebounce.current && organization === '') {
                skipInitialOrgDebounce.current = false;
                return;
            }
            skipInitialOrgDebounce.current = false;

            setFilter((prev) => {
                const next = buildClubFilter(search, organization, prev.role, prev.userStatus);
                if (filtersEqual(prev, next)) {
                    return prev;
                }
                resetPageable();
                return next;
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [organization]);

    const changeRole = (role?: string) => {
        setFilter((prev) => {
            const next = buildClubFilter(search, organization, role, prev.userStatus);
            return filtersEqual(prev, next) ? prev : next;
        });
        resetPageable();
    };

    const changeStatus = (userStatus?: string) => {
        setFilter((prev) => {
            const next = buildClubFilter(search, organization, prev.role, userStatus);
            return filtersEqual(prev, next) ? prev : next;
        });
        resetPageable();
    };

    useEffect(
        () => () => {
            userStore.setClubUsers(null);
        },
        []
    );

    const onDeleteUser = (id: string) => {
        userStore.deleteUser(id);
    };

    const onBlockUser = (id: string) => {
        userStore.blockUser(id);
    };

    const onUnblockUser = (id: string) => {
        userStore.unblockUser(id);
    };

    const activeFilters = hasActiveClubFilters(filter);

    return (
        <Layout page="club" navigation={navigation} error={errorClubUsers} errorScreen>
            <Header
                title="Клуб"
                description={
                    clubUsers
                        ? `Всего ${clubUsers.totalElements} ${strings.ending(
                              clubUsers.totalElements,
                              ['резидент', 'резидента', 'резидентов']
                          )} и амбассадоров`
                        : ''
                }
                activity={
                    <HeaderActivity loading={isLoadingClubUsers} initial={clubUsers === null} />
                }
            />
            <div className="page-users">
                <div className="users-top club-top">
                    <Input
                        ref={inputRef}
                        className="users-search"
                        onChange={(text: string) => setSearch(text)}
                        value={search || ''}
                        type="search"
                        placeholder="Поиск по фамилии, имени, email, телефону"
                    />
                    <Select
                        className="club-filter users-filter-select"
                        placeholder="Роль"
                        allowClear
                        value={filter.role}
                        onChange={changeRole}
                        onClear={() => changeRole(undefined)}
                        options={CLUB_ROLE_CODES.map((code) => ({
                            value: code,
                            label: userRoleLabel(code),
                        }))}
                    />
                    <Input
                        className="club-organization"
                        onChange={(text: string) => setOrganization(text)}
                        value={organization || ''}
                        type="search"
                        placeholder="По компаниям"
                    />
                    <Select
                        className="club-filter users-filter-select"
                        placeholder="Статус"
                        allowClear
                        value={filter.userStatus}
                        onChange={changeStatus}
                        onClear={() => changeStatus(undefined)}
                        options={STATUS_OPTIONS}
                    />
                </div>
                {(clubUsers?.totalElements > 0 || isLoadingClubUsers) && (
                    <UsersTable
                        returnTo="/club"
                        loading={isLoadingClubUsers}
                        onDeleteUser={onDeleteUser}
                        onBlockUser={onBlockUser}
                        onUnblockUser={onUnblockUser}
                        data={clubUsers?.content}
                        page={pageable.page}
                        total={clubUsers?.totalElements}
                        onChangePage={changePage}
                    />
                )}
                {clubUsers?.totalElements === 0 && !isLoadingClubUsers && activeFilters && (
                    <EmptySearch />
                )}
                {clubUsers?.totalElements === 0 && !isLoadingClubUsers && !activeFilters && (
                    <EmptySearch message="Нет резидентов и амбассадоров" />
                )}
                {!clubUsers && errorClubUsers && (
                    <ErrorRequest onClick={() => userStore.getClubUsers(pageable, filter)} />
                )}
            </div>
        </Layout>
    );
});

export default Club;
